import { Pill, Select } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import type { SearchDatasetDto, SearchSort } from '@/shared/api';

interface Props {
  dataset: SearchDatasetDto;
  columns: string[];
  sort: SearchSort[];
  onColumns: (columns: string[]) => void;
  onSort: (sort: SearchSort[]) => void;
}

export function QueryControls({ dataset, columns, sort, onColumns, onSort }: Props) {
  const toggleColumn = (key: string) =>
    onColumns(columns.includes(key) ? columns.filter((c) => c !== key) : [...columns, key]);

  const sortable = dataset.fields.filter((f) => f.sortable);
  const current = sort[0];

  const setSortField = (field: string) =>
    onSort(field ? [{ field, descending: current?.descending ?? false }] : []);
  const setSortDir = (descending: boolean) =>
    current ? onSort([{ field: current.field, descending }]) : undefined;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-ink-soft mb-1.5 text-[13px] font-medium">
          Spalten {columns.length === 0 && <span className="text-ink-faint">(alle)</span>}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {dataset.fields.map((f) => {
            const active = columns.includes(f.key);
            return (
              <button key={f.key} type="button" onClick={() => toggleColumn(f.key)}>
                <Pill variant={active ? 'on' : 'default'} size="sm" className="cursor-pointer">
                  {f.key}
                </Pill>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-ink-soft text-[13px] font-medium">Sortierung</span>
        <Select
          aria-label="Sortierfeld"
          value={current?.field ?? ''}
          onChange={(e) => setSortField(e.target.value)}
          className="min-w-40"
        >
          <option value="">— keine —</option>
          {sortable.map((f) => (
            <option key={f.key} value={f.key}>
              {f.key}
            </option>
          ))}
        </Select>
        {current && (
          <div className="border-line/60 inline-flex overflow-hidden rounded-md border">
            {[
              { label: 'Aufsteigend', desc: false },
              { label: 'Absteigend', desc: true },
            ].map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => setSortDir(opt.desc)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium transition-colors',
                  current.descending === opt.desc
                    ? 'bg-navy text-white'
                    : 'text-ink-soft hover:bg-card-alt',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
